/**
 * Auth-aware fetch wrapper. Automatically includes JWT token from localStorage.
 * Attempts token refresh on 401 responses.
 *
 * Share-link mode: when the URL has ``?share=<token>`` and there's no Bearer
 * token, the share token is appended to API requests so the backend's
 * dual-auth dependencies can authorize the call without forcing login.
 */

import { resolveAuthToken, clearImpersonationToken } from "./resolveAuthToken";

const TOKEN_KEY = "cortex_access_token";
const REFRESH_KEY = "cortex_refresh_token";

function getShareToken(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("share");
  } catch {
    return null;
  }
}

function withShareParam(input: RequestInfo | URL): RequestInfo | URL {
  const share = getShareToken();
  if (!share) return input;
  // Only append to API calls under /api/. Other inputs (full URLs, blobs) untouched.
  const urlStr = typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url;
  if (!urlStr.startsWith("/api/")) return input;
  if (urlStr.includes("share=")) return input;
  const sep = urlStr.includes("?") ? "&" : "?";
  return `${urlStr}${sep}share=${encodeURIComponent(share)}`;
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return false;
  try {
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    return true;
  } catch {
    return false;
  }
}

/** Try It: send a request to an external API through `/api-proxy/forward`.
 *
 * The Cortex credential goes in `X-Cortex-Authorization`, never in
 * `Authorization` — that header belongs to the *target* API (the token the
 * reader typed) and the proxy forwards it untouched, so putting our token
 * there would hand it to whatever host the doc points at. The proxy marks
 * its own auth failure with `X-Cortex-Proxy: auth-required` (a bare 401 is
 * the target's answer), so refresh-and-retry keys off that marker.
 * `redirect: "error"`: a followed redirect would re-send our header to
 * whatever host the target named (the proxy also renames Location). */
export async function proxyFetch(targetUrl: string, init: RequestInit = {}, projectSlug?: string): Promise<Response> {
  const proxyBase = window.location.hostname === "localhost" ? "http://localhost:3001" : "";
  const { kind: resolvedKind } = resolveAuthToken();
  const send = () => {
    const headers = new Headers(init.headers);
    // Header values must be ASCII: URL.href punycodes an IDN host and
    // percent-encodes the path.
    let target = targetUrl;
    try { target = new URL(targetUrl).href; } catch { /* the proxy answers 400 */ }
    headers.set("X-Target-URL", target);
    // The proxy only forwards to API base URLs this project's docs declare,
    // and only for callers who can view the project.
    if (projectSlug) headers.set("X-Cortex-Project", projectSlug);
    // Resolved per send, not hoisted: a successful refresh rewrites the stored
    // access token, and the retry below must pick up the new one.
    const cortexToken = resolveAuthToken().token;
    if (cortexToken) headers.set("X-Cortex-Authorization", `Bearer ${cortexToken}`);
    // Legacy ?share=<token> mode: no JWT at all, the proxy validates the raw share token.
    const legacyShare = !cortexToken ? getShareToken() : null;
    const url = `${proxyBase}/api-proxy/forward${legacyShare ? `?share=${encodeURIComponent(legacyShare)}` : ""}`;
    return fetch(url, { ...init, headers, redirect: "error" });
  };
  let res = await send();
  const proxyAuthFailed = (r: Response) => r.status === 401 && r.headers.get("X-Cortex-Proxy") === "auth-required";
  const unwrap = async (r: Response): Promise<Response> => {
    // The target's real status/headers, as the proxy reported them. The
    // rebuilt Response is plain data for the Try It viewer — the browser
    // never acts on these headers, so Location/Set-Cookie are harmless here.
    const st = r.headers.get("X-Proxy-Status");
    if (!st) return r; // the proxy's own error (400/401/403/413/502)
    const status = Number(st);
    let headers: Headers = r.headers;
    const meta = r.headers.get("X-Proxy-Target-Headers");
    if (meta) {
      try {
        const bytes = Uint8Array.from(atob(meta), (c) => c.charCodeAt(0));
        headers = new Headers();
        for (const [k, v] of JSON.parse(new TextDecoder().decode(bytes)) as [string, string][]) {
          try { headers.append(k, v); } catch { /* not representable — skip */ }
        }
      } catch { headers = r.headers; }
    }
    const nullBody = [101, 103, 204, 205, 304].includes(status);
    const body = nullBody ? null : await r.arrayBuffer();
    try {
      return new Response(body, { status, statusText: r.headers.get("X-Proxy-Status-Text") || "", headers });
    } catch {
      return r;
    }
  };
  // Only a regular login can refresh. A share session has no refresh token,
  // and an impersonation session deliberately has none either.
  if (proxyAuthFailed(res) && resolvedKind === "access" && localStorage.getItem(REFRESH_KEY)) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = tryRefresh();
    }
    const refreshed = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;
    if (refreshed) res = await send();
  }
  return unwrap(res);
}

export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const { token: resolved, kind } = resolveAuthToken();
  const isShareSession = kind === "share";
  const isImpersonating = kind === "impersonation";
  // Legacy ?share=<token> query mode — only when no JWT of any kind is present.
  const isLegacyShareMode = kind === "none" && !!getShareToken();
  const finalInput = isLegacyShareMode ? withShareParam(input) : input;
  const headers = new Headers(init?.headers);
  if (resolved && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${resolved}`);
  }

  let res = await fetch(finalInput, { ...init, headers });

  if (res.status === 401) {
    // An impersonation session has no refresh token by design: when it lapses
    // or is stopped, surface the 401 so the banner can clear itself. Don't
    // bounce to /login — the operator's own session is untouched.
    if (isImpersonating) {
      clearImpersonationToken();
      return res;
    }

    // Share-session JWT expired or revoked: surface the 401 so the
    // caller can render an "expired share" state. Don't bounce to
    // /login — the visitor was never logged in to begin with.
    if (isShareSession || isLegacyShareMode) return res;

    // Try refresh once
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = tryRefresh();
    }
    const refreshed = await refreshPromise;
    isRefreshing = false;
    refreshPromise = null;

    if (refreshed) {
      const newToken = localStorage.getItem(TOKEN_KEY);
      if (newToken) {
        headers.set("Authorization", `Bearer ${newToken}`);
        res = await fetch(finalInput, { ...init, headers });
      }
    } else {
      // Redirect to login
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
  }

  return res;
}
